import React, { useEffect, useState } from 'react';
import { app, pages } from '@microsoft/teams-js';
import TeamsConfigPanel from './TeamsConfigPanel';

// Simple Teams configuration page component
const TeamsConfigPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, _setError] = useState<string>('');
  const [debugMessages, setDebugMessages] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<string>('idle');
  const [saveHandlerRegistered, setSaveHandlerRegistered] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [showUrlConfig, setShowUrlConfig] = useState(false);

  const addDebugMessage = (message: string) => {
    console.log(message);
    setDebugMessages(prev => [...prev.slice(-4), `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const handleUrlChange = (newUrl: string) => {
    setCurrentUrl(newUrl);
    addDebugMessage(`URL changed to: ${newUrl}`);

    pages.config.setConfig({
      entityId: 'sidekick-ai-assistant',
      contentUrl: newUrl + (newUrl.includes('?') ? '&teams=true' : '?teams=true'),
      websiteUrl: newUrl,
      suggestedDisplayName: 'ConnectWise AI',
    });
    addDebugMessage('Teams configuration updated');
  };

  const getCurrentAppUrl = () => {
    return currentUrl || `${window.location.origin}`;
  };

  useEffect(() => {
    addDebugMessage('Component mounted');
    addDebugMessage(`Location: ${window.location.href}`);
    addDebugMessage(`Parent window check: ${window.parent !== window ? 'In iframe' : 'Standalone'}`);

    // Initialize current URL from localStorage or default
    const savedUrl = localStorage.getItem('teams-current-url');
    const initialUrl = savedUrl || `${window.location.origin}`;
    setCurrentUrl(initialUrl);
    addDebugMessage(`Initial URL set to: ${initialUrl}`);

    // TeamsIntegration (parent) already called ensureTeamsInitialized(), so the
    // npm SDK is ready. Register the config page handlers directly.
    const initializeTeams = async () => {
      try {
        addDebugMessage('Configuring Teams SDK (npm)...');

        // Get context for debug info
        try {
          const context = await app.getContext();
          addDebugMessage(`Teams context: ${JSON.stringify(context).substring(0, 100)}...`);
        } catch (err: any) {
          addDebugMessage(`Could not get Teams context: ${err.message}`);
        }

        // Register save handler (only once)
        if (!saveHandlerRegistered) {
          pages.config.registerOnSaveHandler((saveEvent) => {
            addDebugMessage('Save handler triggered');
            setSaveStatus('in-progress');
            try {
              addDebugMessage('Immediately notifying success');
              saveEvent.notifySuccess();
              setSaveStatus('success');
              addDebugMessage('Save completed successfully');
            } catch (err: any) {
              addDebugMessage(`Save handler error: ${err.message}`);
              setSaveStatus('error');
              saveEvent.notifyFailure(err.message || 'Unknown error');
            }
          });
          addDebugMessage('Save handler registered');
          setSaveHandlerRegistered(true);
        } else {
          addDebugMessage('Save handler already registered, skipping');
        }

        // Set valid config to enable save button
        const configUrl = getCurrentAppUrl();
        pages.config.setConfig({
          entityId: 'sidekick-ai-assistant',
          contentUrl: configUrl + (configUrl.includes('?') ? '&teams=true' : '?teams=true'),
          websiteUrl: configUrl,
          suggestedDisplayName: 'ConnectWise AI',
        });
        addDebugMessage(`Configuration set with URL: ${configUrl}`);

        localStorage.setItem('teams-current-url', configUrl);

        pages.config.setValidityState(true);
        addDebugMessage('Save button enabled');
        setIsLoading(false);
      } catch (err: any) {
        addDebugMessage(`Teams initialization error: ${err.message}`);
      }
    };

    const timer = setTimeout(() => {
      void initializeTeams();
    }, 500);

    return () => clearTimeout(timer);
  }, [addDebugMessage]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Initializing Teams configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <h2 className="font-bold text-lg mb-2">Configuration Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center space-x-4 mb-6">
            <svg width="48" height="48" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="w-12 h-12">
              <defs>
                <linearGradient id="tcStarGrad" x1="0" y1="8" x2="16" y2="8" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#38C5F5"/>
                  <stop offset="100%" stopColor="#B0E354"/>
                </linearGradient>
              </defs>
              <path d="M12.4997 9.94253C12.067 10.2382 11.4938 10.1864 11.1249 9.82229C9.71614 8.45662 7.6394 8.06575 5.83061 8.82587C5.35452 9.03108 4.80165 8.87091 4.50598 8.43816C4.21039 8.00552 4.26212 7.43231 4.62614 7.06337C5.99199 5.65465 6.38273 3.57777 5.62256 1.76884C5.4175 1.29281 5.57768 0.740008 6.01035 0.444398C6.44299 0.148806 7.01623 0.200517 7.38516 0.564588C8.794 1.93032 10.8709 2.32127 12.6798 1.56095C13.1558 1.35594 13.7085 1.51616 14.0041 1.94877C14.2997 2.38141 14.248 2.95456 13.884 3.32349C12.5181 4.73222 12.1273 6.80911 12.8875 8.61803C13.0926 9.09407 12.9324 9.64692 12.4997 9.94253Z" fill="url(#tcStarGrad)"/>
              <path d="M1.69207 11.073C1.40787 10.5905 2.00003 10.064 2.44591 10.4028C3.44203 11.1439 4.78952 11.223 5.86558 10.6037C6.34796 10.3196 6.87439 10.9118 6.53574 11.3576C5.79474 12.3537 5.71544 13.7012 6.33481 14.7772C6.619 15.2597 6.02684 15.7862 5.58097 15.4474C4.58484 14.7063 3.23736 14.6272 2.16129 15.2465C1.67892 15.5306 1.15248 14.9384 1.49113 14.4926C2.23213 13.4965 2.31143 12.149 1.69207 11.073Z" fill="url(#tcStarGrad)"/>
            </svg>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">ConnectWise AI Assistant</h1>
              <p className="text-gray-600">AI-powered assistant for ConnectWise users</p>
            </div>
          </div>

          {/* Visual Debug Section */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">Debug Information</h3>
            <div className="text-xs text-blue-700 space-y-1">
              <div>Save Status: <span className="font-mono bg-blue-100 px-1 rounded">{saveStatus}</span></div>
              <div>Messages: {debugMessages.length}</div>
              <div className="max-h-32 overflow-y-auto">
                {debugMessages.map((msg, index) => (
                  <div key={index} className="font-mono text-xs bg-blue-100 p-1 rounded mb-1">
                    {msg}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">

            {/* URL Configuration Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">App Configuration</h2>
                <button
                  onClick={() => setShowUrlConfig(!showUrlConfig)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  {showUrlConfig ? 'Hide Config' : 'Configure URL'}
                </button>
              </div>

              <div className="text-sm text-gray-700 mb-2">
                <strong>Current App URL:</strong>
                <div className="font-mono text-xs bg-white p-2 rounded border mt-1 break-all">
                  {getCurrentAppUrl()}
                </div>
              </div>

              {showUrlConfig && (
                <div className="mt-3">
                  <p className="text-xs text-blue-700 mb-2">
                    Switch between different deployment environments for testing.
                  </p>
                </div>
              )}
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Setup Instructions</h2>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      This will add the ConnectWise AI Assistant as a tab in your Teams channel.
                      Click "Save" to complete the setup.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-md font-medium text-gray-900 mb-2">Features Available:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Documentation Search</h4>
                    <p className="text-xs text-gray-600">ConnectWise University & Virtual Community</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">SOC Operations</h4>
                    <p className="text-xs text-gray-600">Security threat analysis & investigation</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">NOC Procedures</h4>
                    <p className="text-xs text-gray-600">Network operations & troubleshooting</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Workflow Automation</h4>
                    <p className="text-xs text-gray-600">Process automation & optimization</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-md p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Authentication</h3>
              <p className="text-xs text-gray-600">
                The assistant requires ConnectWise SSO authentication for secure access to internal resources.
                You will be prompted to log in with your ConnectWise credentials when accessing the app.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* URL Configuration Panel */}
      {showUrlConfig && (
        <TeamsConfigPanel
          currentUrl={getCurrentAppUrl()}
          onUrlChange={handleUrlChange}
        />
      )}
    </div>
  );
};

export default TeamsConfigPage;
